"use client";

import { useState, useEffect } from "react";
import {
  Send,
  X,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { postSolicitudInformacion } from "@/lib/api/datosGenerales/postSolicitudInformacion";
import type { SolicitudFormData } from "@/lib/api/datosGenerales/types/solicitudInformacion";
import { TIPOS_IDENTIFICACION } from "../data/constants";
import { fetchListaPaises } from "@/lib/api/datosGenerales/fetchListaPaises";
import type { ListaPaises } from "@/lib/api/datosGenerales/types/listaPaises";

interface SolicitudInformacionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialFormData: SolicitudFormData = {
  nombre: "",
  identificacion_tipo: "",
  identificacion_numero: "",
  email: "",
  telefono: "",
  pais_codigo: "",
  asunto: "",
  descripcion_solicitud: "",
  adjunto: null,
};

export default function SolicitudInformacionModal({
  isOpen,
  onClose,
}: SolicitudInformacionModalProps) {
  const [formData, setFormData] = useState<SolicitudFormData>(initialFormData);
  const [listaPaises, setListaPaises] = useState<ListaPaises[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [fileName, setFileName] = useState<string>("");

  useEffect(() => {
    const loadPaises = async () => {
      try {
        const paises = await fetchListaPaises();
        setListaPaises(paises);
      } catch (err) {
        console.error("Error cargando lista de países:", err);
      }
    };

    loadPaises();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFileName("");
    setSelectedFile(null);
    setSubmitStatus(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Enviar datos + archivo juntos
    const result = await postSolicitudInformacion(formData, selectedFile);

    setSubmitStatus({
      type: result.success ? "success" : "error",
      message: result.message,
    });

    if (result.success) {
      // Esperar un momento para mostrar el mensaje de éxito y luego cerrar
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    }

    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isFormValid =
    formData.nombre.trim() !== "" &&
    formData.identificacion_tipo !== "" &&
    formData.identificacion_numero.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.telefono.trim() !== "" &&
    formData.pais_codigo !== "" &&
    formData.asunto.trim() !== "" &&
    formData.descripcion_solicitud.trim() !== "";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden mx-4">
        {/* Header */}
        <div className="bg-linear-to-r from-[#001689] to-[#3b49bf] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Send className="w-5 h-5 text-white" />
              <h2 className="text-xl font-semibold text-white">
                Solicitar Información
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nombre */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="Ingrese su nombre completo"
              />
            </div>

            {/* Tipo de Identificación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de identificación <span className="text-red-500">*</span>
              </label>
              <select
                name="identificacion_tipo"
                value={formData.identificacion_tipo}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white"
              >
                <option value="">Seleccione...</option>
                {TIPOS_IDENTIFICACION.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Número de Identificación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de documento <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="identificacion_numero"
                value={formData.identificacion_numero}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="Ej: 8-888-888"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correo electrónico <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="correo@ejemplo.com"
              />
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="Ej: 6000-0000"
              />
            </div>

            {/* País */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                País <span className="text-red-500">*</span>
              </label>
              <select
                name="pais_codigo"
                value={formData.pais_codigo}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white"
              >
                <option value="">Seleccione un país...</option>
                {listaPaises.map((pais) => (
                  <option key={pais.codigo} value={pais.codigo}>
                    {pais.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Asunto */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asunto <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="asunto"
                value={formData.asunto}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="Asunto de su solicitud"
              />
            </div>

            {/* Descripción */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción de la solicitud{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="descripcion_solicitud"
                value={formData.descripcion_solicitud}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                placeholder="Describa detalladamente la información que desea solicitar..."
              />
            </div>

            {/* Adjunto */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adjuntar archivo (opcional)
              </label>
              <div className="relative">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="file-upload"
                  className={`flex items-center gap-3 w-full px-4 py-3 border-2 border-dashed rounded-lg transition-all cursor-pointer ${
                    fileName
                      ? "border-green-300 bg-green-50/50"
                      : "border-gray-200 hover:border-blue-400 hover:bg-blue-50/50"
                  }`}
                >
                  {fileName ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">{fileName}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        Haga clic para seleccionar un archivo
                      </span>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Formatos permitidos: PDF, DOC, DOCX, JPG, PNG
              </p>
            </div>
          </div>

          {/* Status Message */}
          {submitStatus && (
            <div
              className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
                submitStatus.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {submitStatus.type === "success" ? (
                <CheckCircle className="w-5 h-5 shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 shrink-0" />
              )}
              <span className="text-sm">{submitStatus.message}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2.5 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#001689] text-white rounded-lg hover:bg-[#000f61] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar solicitud
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
